import React, { useState, useEffect } from "react";
import {
  AlertCircle,
  Mail,
  Lock,
  Eye,
  EyeOff,
  RefreshCcw,
  CheckCircle,
} from "lucide-react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { getEmailValidationError } from "../../utils/emailValidation";
import { loadPmcExperience } from "../../api/pmcExperienceService";
import PmcCoBrand from "../common/PmcCoBrand";
import Toast from "../client/Toast";
import LoadingSpinner from "../common/LoadingSpinner";
import { setTOTPVerified } from "../../utils/totpSession";
import {
  loadSalesforceChat,
  unloadSalesforceChat,
} from "../../utils/salesforceChat";
import { useEnvironment } from "../../hooks/useEnvironment";

const RESEND_COOLDOWN_SECONDS = 60;
const OTP_SENT_MESSAGE =
  "Check your email for the one-time password. Be sure to also check your spam folder.";
const OTP_RESENT_MESSAGE =
  "We sent a new one-time password to your email. Be sure to also check your spam folder.";

type LoginStage =
  | "email"
  | "password"
  | "verification_required"
  | "invitation_resent"
  | "not_invited";

const LoginPage: React.FC = () => {
  const { selfServeSignup } = useEnvironment();
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>("Signing in...");
  const [email, setEmail] = useState<string>("");
  const [stage, setStage] = useState<LoginStage>("email");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showOtpInput, setShowOtpInput] = useState<boolean>(false);
  const [otpCode, setOtpCode] = useState<string>("");
  const [tempToken, setTempToken] = useState<string>("");
  const [resendCooldown, setResendCooldown] = useState<number>(0);
  const [verificationCooldown, setVerificationCooldown] = useState<number>(0);
  const [verificationSent, setVerificationSent] = useState<boolean>(false);
  const [toast, setToast] = useState<{
    isVisible: boolean;
    message: string;
    type: "success" | "error" | "info" | "warning";
  }>({
    isVisible: false,
    message: "",
    type: "success",
  });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const showToast = (
    message: string,
    type: "success" | "error" | "info" | "warning" = "success",
  ): void => {
    setToast({ isVisible: true, message, type });
  };

  const hideToast = (): void => {
    setToast({ isVisible: false, message: "", type: "success" });
  };

  const getApiBase = () => {
    return typeof window !== "undefined" &&
      window.location.hostname === "localhost"
      ? "http://localhost:8000/api/"
      : import.meta.env.VITE_API_URL ||
          "https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/";
  };

  // Pre-fill email from query (?email=) or verification callback
  useEffect(() => {
    const verifiedEmail = sessionStorage.getItem("verifiedEmail");
    if (verifiedEmail) {
      setEmail(verifiedEmail);
      sessionStorage.removeItem("verifiedEmail");
      showToast(
        "Email verified! Please sign in with your password.",
        "success",
      );
      return;
    }

    const fromQuery = searchParams.get("email")?.trim();
    if (fromQuery) {
      setEmail(fromQuery);
    }

    const hint = searchParams.get("hint");
    if (hint === "invite-consumed") {
      showToast(
        "Your account is already set up. Sign in with the password you created.",
        "info",
      );
    } else if (hint === "session-expired") {
      showToast("Session expired, please sign in again.", "warning");
    }
  }, [searchParams]);

  useEffect(() => {
    loadSalesforceChat();
    return () => {
      unloadSalesforceChat();
    };
  }, []);

  // Tick down the resend cooldown once per second.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (verificationCooldown <= 0) return;
    const timer = setInterval(() => {
      setVerificationCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [verificationCooldown]);

  const formatCooldown = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handleEmailDiscovery = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const emailError = getEmailValidationError(email);
    if (emailError) {
      setError(emailError);
      return;
    }

    const normalizedEmail = email.trim();
    setEmail(normalizedEmail);
    setLoadingMessage("Checking your account...");
    setLoading(true);

    try {
      const baseUrl = getApiBase();
      const response = await fetch(`${baseUrl}auth/email-discovery`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalizedEmail }),
      });
      const data = await response.json();

      if (!response.ok) {
        setError(
          typeof data.detail === "string"
            ? data.detail
            : "We could not check this email. Please try again.",
        );
        return;
      }

      if (data.state === "password_required") {
        setStage("password");
        return;
      }

      if (
        data.state === "verification_required" ||
        data.state === "invitation_resent" ||
        data.state === "not_invited"
      ) {
        setStage(data.state);
        return;
      }

      setError("Unexpected account response. Please contact support.");
    } catch (error: any) {
      console.error("Email discovery error:", error);
      setError(`Unable to continue: ${error.message || "Please try again."}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!password.trim()) {
      setError("Please enter your password");
      return;
    }

    setLoadingMessage("Signing in...");
    setLoading(true);

    try {
      const baseUrl = getApiBase();
      const formData = new URLSearchParams();
      formData.append("username", email);
      formData.append("password", password);

      const response = await fetch(`${baseUrl}auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData.toString(),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(
          typeof data.detail === "string"
            ? data.detail
            : "Invalid email or password",
        );
        setLoading(false);
        return;
      }

      // An email one-time passcode was sent; show the code entry screen.
      if (data.requires_otp && data.temp_token) {
        setTempToken(data.temp_token);
        // Email-first per-account theming: the account is known once the
        // password verifies, so apply the PMC brand before the OTP step.
        // loadPmcExperience is default-safe (falls back to the unbranded shell).
        await loadPmcExperience(data.account_id);
        setShowOtpInput(true);
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        setLoading(false);
        showToast(OTP_SENT_MESSAGE, "info");
        return;
      }

      // Fallback: a session token was returned directly (no OTP step).
      if (data.access_token) {
        localStorage.setItem("authToken", data.access_token);
        setTOTPVerified();
        navigate("/dashboard");
      } else {
        setError("Unexpected login response. Please contact support.");
        setLoading(false);
      }
    } catch (error: any) {
      console.error("Login error:", error);
      setError(`Sign-in failed: ${error.message || "Please try again."}`);
    } finally {
      if (!showOtpInput) {
        setLoading(false);
      }
    }
  };

  const handleSendVerification = async () => {
    if (verificationCooldown > 0) return;
    setError("");
    setLoadingMessage("Sending verification email...");
    setLoading(true);

    try {
      const response = await fetch(
        `${getApiBase()}users/send-verification-email`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        },
      );
      const data = await response.json();

      if (!response.ok) {
        setError(
          typeof data.detail === "string"
            ? data.detail
            : "Could not send a verification email. Please try again.",
        );
        return;
      }

      setVerificationSent(true);
      setVerificationCooldown(
        typeof data.cooldown_seconds === "number"
          ? data.cooldown_seconds
          : RESEND_COOLDOWN_SECONDS,
      );
    } catch (error: any) {
      console.error("Verification resend error:", error);
      setError(
        `Could not send a verification email: ${error.message || "Please try again."}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!otpCode.trim()) {
      setError("Please enter the code from your email");
      return;
    }

    if (otpCode.length !== 6) {
      setError("The code must be 6 digits");
      return;
    }

    setLoadingMessage("Verifying code...");
    setLoading(true);

    try {
      const baseUrl = getApiBase();

      const response = await fetch(`${baseUrl}auth/verify-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          temp_token: tempToken,
          otp_code: otpCode,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Backend returns a structured detail: { error_code, message, attempts_remaining }
        const detail = data.detail;
        const errorCode =
          typeof detail === "object" ? detail?.error_code : undefined;
        const message = typeof detail === "object" ? detail?.message : detail;

        if (errorCode === "code_expired") {
          setOtpCode("");
          setError(
            message || "This code has expired. Please request a new one.",
          );
          showToast(
            'Your code has expired. Tap "Resend code" to get a new one.',
            "error",
          );
        } else if (errorCode === "code_invalid") {
          setError(message || "That code is incorrect. Please try again.");
        } else {
          setError(
            typeof message === "string"
              ? message
              : "Invalid code. Please try again.",
          );
        }
        setLoading(false);
        return;
      }

      if (data.access_token) {
        localStorage.setItem("authToken", data.access_token);
        // Mark the session as verified (reused from the previous 2FA flow).
        setTOTPVerified();

        setTimeout(() => {
          setLoading(false);
          navigate("/dashboard");
        }, 100);
      } else {
        setError("Login failed: No access token received");
        setLoading(false);
      }
    } catch (error: any) {
      console.error("❌ OTP verification error:", error);
      setError(`Verification failed: ${error.message || "Please try again."}`);
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setError("");

    try {
      const baseUrl = getApiBase();
      const response = await fetch(`${baseUrl}auth/resend-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ temp_token: tempToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        const detail = data.detail;
        if (
          typeof detail === "object" &&
          detail?.error_code === "resend_too_soon"
        ) {
          // Keep the countdown running using the server's retry_after.
          setResendCooldown(detail.retry_after || RESEND_COOLDOWN_SECONDS);
          return;
        }
        const message = typeof detail === "object" ? detail?.message : detail;
        setError(
          typeof message === "string"
            ? message
            : "Could not resend the code. Please try again.",
        );
        return;
      }

      setOtpCode("");
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      showToast(OTP_RESENT_MESSAGE, "info");
    } catch (error: any) {
      console.error("❌ OTP resend error:", error);
      setError(
        `Could not resend the code: ${error.message || "Please try again."}`,
      );
    }
  };

  // Show the email OTP verification form if needed.
  // Mirrors the admin OtpVerificationStep layout; branding-aware (per-account
  // logo + theme already applied via loadPmcExperience before this renders).
  if (showOtpInput) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-3xl shadow-xl p-12 md:p-16">
          <div className="flex flex-col items-center mb-10">
            <div className="mb-6 flex justify-center">
              {/* Honors logoMode: 'replace' -> PMC only; 'cobrand' (default) ->
                  PMC × GetPawtected; no PMC brand -> GetPawtected only. */}
              <PmcCoBrand
                imgClassName="h-14 w-auto"
                pmcMaxWidth="max-w-[240px]"
                gpClassName="h-9 w-auto"
              />
            </div>
            <h1 className="text-3xl md:text-3xl font-medium font-poppins text-black mb-3 text-center">
              Enter the 6-digit code
            </h1>
            <p className="text-base md:text-lg text-gray-600 text-center font-inter">
              We emailed a sign-in code to{" "}
              <span className="font-semibold text-gray-800">
                {email || "your email"}
              </span>
              . It expires in a few minutes.
            </p>
          </div>

          {error && (
            <div className="mb-6 p-5 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700 font-inter">{error}</p>
            </div>
          )}

          <form onSubmit={handleOtpVerification} className="space-y-6">
            <div>
              <label
                htmlFor="otp"
                className="block text-base font-medium text-gray-700 mb-2 font-inter"
              >
                Verification code
              </label>
              <input
                id="otp"
                name="otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={otpCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setOtpCode(value);
                  setError("");
                }}
                placeholder="123456"
                className="w-full text-center tracking-[0.5em] text-2xl font-poppins px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:border-[color:var(--pmc-accent)] focus:ring-1 focus:ring-[color:var(--pmc-accent)]"
                maxLength={6}
                autoFocus
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading || otpCode.length !== 6}
              className="w-full py-5 bg-[color:var(--pmc-accent)] text-white font-semibold rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-inter text-lg shadow-sm flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Verifying…</span>
                </>
              ) : (
                <span>Verify and continue</span>
              )}
            </button>
          </form>

          <div className="mt-6 flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={resendCooldown > 0}
              className="inline-flex items-center gap-2 text-sm text-[color:var(--pmc-accent)] hover:opacity-80 font-medium font-inter disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCcw className="w-4 h-4" />
              {resendCooldown > 0
                ? `Resend code in ${formatCooldown(resendCooldown)}`
                : "Resend code"}
            </button>
          </div>
        </div>

        {/* Toast Notification */}
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={hideToast}
        />
      </div>
    );
  }

  // Main login form
  return (
    <>
      {/* Full-screen loading overlay with paw animation */}
      {loading && (
        <div
          className="fixed inset-0 bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center z-50"
          role="alert"
          aria-live="assertive"
        >
          <LoadingSpinner size="large" text={loadingMessage} />
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <main
          id="main-content"
          className="max-w-2xl w-full bg-white rounded-3xl shadow-xl p-12 md:p-16"
        >
          <div className="flex flex-col items-center mb-12">
            <div className="mb-6">
              <img
                src="/landing/getpawtected.svg"
                alt="GetPawtected logo"
                className="w-64 md:w-80 lg:w-96 h-auto"
              />
            </div>
            <h1 className="text-4xl md:text-3xl font-medium font-poppins text-black mb-4 text-center">
              Welcome!
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 text-center font-inter leading-relaxed max-w-md">
              Sign in to manage your pet's insurance and protection
            </p>
          </div>

          {error && (
            <div
              className="mb-6 p-6 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-4"
              role="alert"
              aria-live="assertive"
            >
              <AlertCircle
                className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0"
                aria-hidden="true"
              />
              <p className="text-base text-red-700 font-inter">{error}</p>
            </div>
          )}

          {stage === "email" && (
            <form
              onSubmit={handleEmailDiscovery}
              className="space-y-6"
              aria-label="Continue with email"
            >
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2 font-inter"
                >
                  Email Address
                </label>
                <div className="relative">
                  <Mail
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                    aria-hidden="true"
                  />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError("");
                    }}
                    className="w-full pl-12 pr-4 py-4 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-[color:var(--pmc-accent)] transition-colors font-inter text-base"
                    placeholder="Enter your email"
                    required
                    aria-required="true"
                    aria-invalid={error ? "true" : "false"}
                    autoComplete="email"
                    autoFocus
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-5 px-8 bg-[color:var(--pmc-accent)] text-white rounded-full hover:bg-[color:var(--pmc-accent-hover)] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 font-poppins text-lg font-medium focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-accent)] focus:ring-offset-2"
              >
                Continue
              </button>
            </form>
          )}

          {stage === "password" && (
            <form
              onSubmit={handleEmailSignIn}
              className="space-y-6"
              aria-label="Sign in form"
            >
              <div className="rounded-xl border-2 border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-gray-500 font-inter">
                  Signing in as
                </p>
                <p className="truncate text-base font-medium text-gray-800 font-inter">
                  {email}
                </p>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700 font-inter"
                  >
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-[color:var(--pmc-accent)] hover:underline font-inter focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-accent)] focus:ring-offset-2 rounded"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative rounded-xl border-2 border-gray-200 bg-gray-50 transition-colors focus-within:border-[color:var(--pmc-accent)]">
                  <Lock
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                    aria-hidden="true"
                  />
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError("");
                    }}
                    className="w-full rounded-xl border-0 bg-transparent pl-12 pr-12 py-3 focus:outline-none focus:ring-0 font-inter text-base text-gray-800 placeholder:text-gray-400"
                    placeholder="Enter your password"
                    required
                    aria-required="true"
                    aria-invalid={error ? "true" : "false"}
                    autoComplete="current-password"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-accent)] rounded"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" aria-hidden="true" />
                    ) : (
                      <Eye className="w-5 h-5" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-5 px-8 bg-[color:var(--pmc-accent)] text-white rounded-full hover:bg-[color:var(--pmc-accent-hover)] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 font-poppins text-lg font-medium focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-accent)] focus:ring-offset-2"
                aria-label={
                  loading
                    ? "Signing in, please wait"
                    : "Sign in to your account"
                }
              >
                Sign In
              </button>
            </form>
          )}

          {stage === "verification_required" && (
            <div className="space-y-6 text-center">
              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-6">
                {verificationSent && (
                  <CheckCircle
                    className="mx-auto mb-3 h-10 w-10 text-green-600"
                    aria-hidden="true"
                  />
                )}
                <h2 className="text-xl font-medium text-gray-900 font-poppins">
                  {verificationSent
                    ? "Request received"
                    : "Verify your email to continue"}
                </h2>
                <p className="mt-3 text-base text-gray-600 font-inter">
                  {verificationSent
                    ? `Check your inbox and spam folder for the latest verification email for ${email}. It may take a few minutes to arrive.`
                    : "Your account must be verified before you can sign in or reset your password."}
                </p>
              </div>
              <button
                type="button"
                onClick={handleSendVerification}
                disabled={loading || verificationCooldown > 0}
                className="w-full py-5 px-8 bg-[color:var(--pmc-accent)] text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed font-poppins text-lg font-medium"
              >
                {verificationCooldown > 0
                  ? `Send again in ${formatCooldown(verificationCooldown)}`
                  : "Send new verification email"}
              </button>
            </div>
          )}

          {stage === "invitation_resent" && (
            <div className="space-y-6 text-center">
              <div className="rounded-2xl bg-green-50 border border-green-200 p-6">
                <CheckCircle
                  className="mx-auto mb-3 h-10 w-10 text-green-600"
                  aria-hidden="true"
                />
                <h2 className="text-xl font-medium text-gray-900 font-poppins">
                  Check your inbox for the latest invitation
                </h2>
                <p className="mt-3 text-base text-gray-600 font-inter">
                  Use the most recent invitation link for {email} to complete
                  registration before signing in. If it has not arrived yet,
                  allow a few minutes and check your spam folder.
                </p>
              </div>
            </div>
          )}

          {stage === "not_invited" && (
            <div className="space-y-6 text-center">
              <div className="rounded-2xl bg-gray-50 border border-gray-200 p-6">
                <h2 className="text-xl font-medium text-gray-900 font-poppins">
                  No invitation was found
                </h2>
                <p className="mt-3 text-base text-gray-600 font-inter">
                  Ask your property manager or administrator to invite {email}{" "}
                  before trying again.
                </p>
              </div>
            </div>
          )}

          {/* Sign Up Link — self-serve sign-up: dev/local only; hard-gated on staging/prod. */}
          {selfServeSignup && (
            <div className="mt-8 text-center">
              <p className="text-base text-gray-600 font-inter">
                Don't have an account?{" "}
                <Link
                  to="/signup"
                  className="text-[color:var(--pmc-accent)] hover:underline font-medium focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-accent)] focus:ring-offset-2 rounded"
                >
                  Sign up
                </Link>
              </p>
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-base text-gray-500 font-inter leading-relaxed">
              By signing in, you agree to our{" "}
              <a
                href="/terms-of-service"
                className="text-[color:var(--pmc-accent)] hover:underline font-medium focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-accent)] focus:ring-offset-2 rounded"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                href="/privacy-policy"
                className="text-[color:var(--pmc-accent)] hover:underline font-medium focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-accent)] focus:ring-offset-2 rounded"
              >
                Privacy Policy
              </a>
            </p>
          </div>

          <div className="mt-6 text-center">
            <p className="text-gray-400 font-inter text-sm">
              Pet Registration & Insurance Management System
            </p>
          </div>
        </main>

        {/* Toast Notification */}
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={hideToast}
        />
      </div>
    </>
  );
};

export default LoginPage;
